/**
 * analytics.js — App B (React 17 CRA)
 *
 * Centralised Consent Mode v2 + GA4 helpers.
 * All tracking is gated: no data is sent until the user accepts.
 *
 * Strategy: Option A — GA loads immediately with all consent types
 * defaulting to "denied" (set in public/index.html BEFORE gtag.js loads).
 * Consent is updated here when the user makes a decision.
 */

/** localStorage key for persisted consent */
export const CONSENT_KEY = 'clockhash_consent';

// Google Analytics Measurement ID fallback
const GA_ID = process.env.REACT_APP_GA_ID || 'G-V8FE9EFRPY';

// Dynamically inject the GA4 script tag and configure gtag
if (typeof window !== 'undefined') {
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false });
}

/**
 * Increment this string whenever privacy policy / cookie categories change.
 * Stored consent with a different version will be ignored, forcing re-prompt.
 */
export const CONSENT_VERSION = '1';

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

/**
 * Read stored consent from localStorage.
 * Returns null if nothing is stored or if the version has changed.
 */
export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null; // force re-prompt
    return parsed; // { decision: 'accepted' | 'declined', version, timestamp }
  } catch {
    return null;
  }
}

/** Persist the user's decision. */
export function storeConsent(decision) {
  localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify({ decision, version: CONSENT_VERSION, timestamp: Date.now() })
  );
}

/** Clear stored consent so the banner re-appears on next load. */
export function clearConsent() {
  localStorage.removeItem(CONSENT_KEY);
}

// ---------------------------------------------------------------------------
// Consent Mode v2
// ---------------------------------------------------------------------------

/** Safe wrapper around window.gtag */
function gtag(...args) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag(...args);
}

/**
 * Update all four Consent Mode v2 signals.
 * Call this when the user accepts or declines the banner.
 * @param {boolean} granted
 */
export function updateConsent(granted) {
  const state = granted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    analytics_storage: state,
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
  });
}

// ---------------------------------------------------------------------------
// Event helpers
// ---------------------------------------------------------------------------

/**
 * Send a GA4 page_view event.
 * Only fires data when analytics_storage = granted.
 */
export function sendPageView(path) {
  gtag('event', 'page_view', {
    page_path: path || window.location.pathname + window.location.search,
  });
}

/**
 * Send a custom GA4 event.
 * Only fires data when analytics_storage = granted.
 * @param {string} eventName
 * @param {object} [params]
 */
export function sendEvent(eventName, params = {}) {
  gtag('event', eventName, params);
}
